import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, delay } from 'rxjs/operators';
import { PCP_LOGGER } from '@pcp/logging';

/**
 * Configuration interface for retry strategies
 */
export interface RetryConfig {
    maxAttempts: number;
    baseDelayMs: number;
    maxDelayMs: number;
    networkErrorDelayMs: number;
}

/**
 * Service responsible for implementing retry strategies for authentication operations.
 * Provides configurable retry logic with exponential backoff and error classification.
 */
@Injectable({
    providedIn: 'root'
})
export class AuthRetryStrategyService {

    private static readonly DEFAULT_CONFIG: RetryConfig = {
        maxAttempts: 10,
        baseDelayMs: 1000,
        maxDelayMs: 30000,
        networkErrorDelayMs: 2000
    };

    private readonly log = inject(PCP_LOGGER).createScoped('auth');

    /**
     * Create a retry operator for token refresh operations.
     * Distinguishes between network errors (which should be retried) and auth errors (which should not).
     */
    createTokenRefreshRetryStrategy(config: Partial<RetryConfig> = {}): <T>(source: Observable<T>) => Observable<T> {
        const retryConfig = { ...AuthRetryStrategyService.DEFAULT_CONFIG, ...config };

        return <T>(source: Observable<T>) => source.pipe(
            catchError((error, caught) => {
                // For auth errors (401, 403), don't retry - these indicate invalid credentials/tokens
                if (this.isAuthError(error)) {
                    this.log.debug('Auth error - not retrying', { error });
                    return throwError(() => error);
                }

                // For network errors, retry with appropriate delay
                if (this.isNetworkError(error)) {
                    this.log.debug('Network error detected, will retry with delay', { error });
                    return caught.pipe(delay(retryConfig.networkErrorDelayMs));
                }

                // For other errors (server errors, etc.), don't retry
                this.log.debug('Non-retryable error', { error });
                return throwError(() => error);
            })
        );
    }

    /**
     * Create a retry operator with exponential backoff.
     * Useful for operations that should retry with increasing delays.
     */
    createExponentialBackoffRetryStrategy(config: Partial<RetryConfig> = {}): <T>(source: Observable<T>) => Observable<T> {
        const retryConfig = { ...AuthRetryStrategyService.DEFAULT_CONFIG, ...config };
        let attemptCount = 0;

        return <T>(source: Observable<T>) => source.pipe(
            catchError((error, caught) => {
                attemptCount++;

                // Exceed max attempts
                if (attemptCount >= retryConfig.maxAttempts) {
                    this.log.warn(`Max retry attempts (${retryConfig.maxAttempts}) exceeded`);
                    return throwError(() => error);
                }

                // Don't retry auth errors
                if (this.isAuthError(error)) {
                    this.log.debug('Auth error - not retrying', { error });
                    return throwError(() => error);
                }

                // Calculate exponential backoff delay
                const exponentialDelay = Math.min(
                    retryConfig.baseDelayMs * Math.pow(2, attemptCount - 1),
                    retryConfig.maxDelayMs
                );

                this.log.debug(`Retry attempt ${attemptCount}/${retryConfig.maxAttempts} in ${exponentialDelay}ms`);
                
                return caught.pipe(delay(exponentialDelay));
            })
        );
    }

    /**
     * Check if an error is an authentication error that should not be retried.
     */
    private isAuthError(error: any): boolean {
        if (error instanceof HttpErrorResponse) {
            // Authentication and authorization errors should not be retried
            return error.status === 401 || error.status === 403;
        }
        return false;
    }

    /**
     * Check if an error is a network-related error that might be temporary.
     */
    private isNetworkError(error: any): boolean {
        if (error instanceof HttpErrorResponse) {
            // Network-related HTTP status codes that might be temporary
            return error.status === 0 ||     // Connection failed
                   error.status === 408 ||   // Request timeout
                   error.status === 429 ||   // Too many requests
                   error.status === 503 ||   // Service unavailable
                   error.status === 504;     // Gateway timeout
        }

        // Other network errors (timeout, connection refused, etc.)
        return error.name === 'AbortError' ||
               error.name === 'TimeoutError' ||
               error.message?.includes('fetch') ||
               error.message?.includes('network') ||
               error.message?.includes('timeout') ||
               error.message?.includes('connection');
    }

    /**
     * Get a user-friendly description of the error type for logging.
     */
    getErrorTypeDescription(error: any): string {
        if (this.isAuthError(error)) {
            return 'Authentication Error';
        }
        if (this.isNetworkError(error)) {
            return 'Network Error';
        }
        return 'Unknown Error';
    }
}